/**
 * Learner side navigation — floating menu control + slide-in drawer.
 * Profile, messages, favorites, library, and public pages live here (not bottom banner).
 */

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  BookOpen,
  ChevronDown,
  FileText,
  Heart,
  Home,
  LayoutDashboard,
  Library,
  LucideIcon,
  Mail,
  MessageCircle,
  Send,
  SquareStack,
  User,
} from 'lucide-react';
import { messageService } from '../../services/messageService';
import { appReleaseService } from '../../services/appReleaseService';
import { useTranslation } from 'react-i18next';
import { useAppLocale } from '../../providers/LocaleProvider';
import { LanguageSwitcher } from './LanguageSwitcher';
import { useResponsive } from './MobileResponsive';
import { AppBrandLogo } from './AppBrandLogo';
import { isProductTourUiNode } from '../../learnerTour/productTourUi';
import { useTourCollapsibleTarget } from '../../learnerTour/tourTargetEvents';
import { useHistoryBackClose } from '../../hooks/useHistoryBackClose';
import { CollapsibleFadePanel } from './CollapsibleFadePanel';
import { useLearnerHomeDesign } from '../../utils/learnerHomeDesign';
import { LEITNER_CHANGED_EVENT, leitnerService } from '../../services/leitnerService';
import { LEITNER_OPEN_EVENT } from '../leitner/LeitnerBoxPanel';
import { LEARNER_OPEN_CHAT_EVENT, LEARNER_OPEN_TELEGRAM_EVENT } from './LearnerEdgeActions';

export type StaticPageKey = 'About' | 'Contact' | 'Privacy' | 'Terms';

const publicPagesPanelId = 'learner-side-nav-public-pages';

function usePublicPageLinks(): { key: StaticPageKey; label: string }[] {
  const { t } = useTranslation('nav');
  return [
    { key: 'About', label: t('about') },
    { key: 'Contact', label: t('contact') },
    { key: 'Privacy', label: t('privacy') },
    { key: 'Terms', label: t('terms') },
  ];
}

export interface LearnerSideNavProps {
  user?: { fullName?: string; email?: string; profilePhoto?: string } | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  panelId: string;
  onHomeClick?: () => void;
  onDashboardClick?: () => void;
  onOpenLibrary?: () => void;
  onOpenStaticPage?: (key: StaticPageKey) => void;
  onNavigateProfile?: (hash: 'profile' | 'messages' | 'favorites' | 'menus') => void;
  onOpenCourses?: () => void;
}

export const LearnerSideNav: React.FC<LearnerSideNavProps> = ({
  user,
  open,
  onOpenChange,
  panelId,
  onHomeClick,
  onDashboardClick,
  onOpenLibrary,
  onOpenStaticPage,
  onNavigateProfile,
  onOpenCourses,
}) => {
  const { t } = useTranslation('nav');
  const { t: tCommon } = useTranslation('common');
  const { t: tLeitner } = useTranslation('leitner');
  const { direction } = useAppLocale();
  const [homeDesign] = useLearnerHomeDesign();
  const { isMobile } = useResponsive();
  const simpleHome = homeDesign === 'mme';
  const showEdgeTools = simpleHome || isMobile;
  const [leitnerDue, setLeitnerDue] = useState(0);
  const isRtl = direction === 'rtl';
  const publicPageLinks = usePublicPageLinks();
  const shellRef = useRef<HTMLDivElement>(null);
  const [publicExpanded, setPublicExpanded] = useState(true);
  useTourCollapsibleTarget('side-nav-public-pages', publicExpanded, setPublicExpanded);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [versionLabel, setVersionLabel] = useState<string | null>(null);

  useEffect(() => {
    if (!showEdgeTools) return undefined;
    const load = () => {
      void leitnerService.getSummary().then((summary) => setLeitnerDue(summary.dueCount)).catch(() => undefined);
    };
    load();
    window.addEventListener(LEITNER_CHANGED_EVENT, load);
    return () => window.removeEventListener(LEITNER_CHANGED_EVENT, load);
  }, [showEdgeTools]);

  const close = useCallback(() => onOpenChange(false), [onOpenChange]);
  useHistoryBackClose(open, close, 'learner-side-nav');

  const prevDirectionRef = useRef(direction);
  const directionJustChanged = prevDirectionRef.current !== direction;
  const [swapHidden, setSwapHidden] = useState(false);

  useLayoutEffect(() => {
    if (prevDirectionRef.current === direction) return undefined;
    prevDirectionRef.current = direction;
    close();
    setSwapHidden(true);
    return undefined;
  }, [direction, close]);

  useLayoutEffect(() => {
    if (!swapHidden || open) return undefined;
    const frame = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => setSwapHidden(false));
    });
    return () => window.cancelAnimationFrame(frame);
  }, [swapHidden, open]);

  const uiOpen = open && !directionJustChanged && !swapHidden;
  const shellMotionClass = swapHidden || directionJustChanged
    ? 'transition-none'
    : 'transition-transform duration-200 ease-out';
  const backdropMotionClass = swapHidden || directionJustChanged
    ? 'transition-none'
    : 'transition-opacity duration-200 ease-out';

  const runAndClose = useCallback(
    (fn?: () => void) => {
      fn?.();
      close();
    },
    [close]
  );

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (shellRef.current?.contains(target)) return;
      if (target instanceof Element && target.closest('[data-learner-nav-toggle]')) return;
      if (isProductTourUiNode(target)) return;
      close();
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open, close]);

  useEffect(() => {
    const loadUnread = async () => {
      try {
        setUnreadMessageCount(await messageService.getUnreadCount());
      } catch {
        setUnreadMessageCount(0);
      }
    };
    void loadUnread();
    const interval = setInterval(loadUnread, 30000);
    const refresh = () => {
      void loadUnread();
      window.setTimeout(loadUnread, 500);
    };
    window.addEventListener('messageRead', refresh);
    window.addEventListener('refreshInbox', refresh);
    return () => {
      clearInterval(interval);
      window.removeEventListener('messageRead', refresh);
      window.removeEventListener('refreshInbox', refresh);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const info = await appReleaseService.getPwaReleaseInfo();
      if (cancelled) return;
      if (info.versionLabel?.trim()) {
        setVersionLabel(info.versionLabel.trim());
        return;
      }
      const releases = await appReleaseService.getPublishedReleases(1);
      if (!cancelled && releases[0]?.versionLabel) {
        setVersionLabel(releases[0].versionLabel);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const displayName =
    (user?.fullName && user.fullName.trim()) ||
    (user?.email && user.email.split('@')[0]) ||
    t('learnerDefault');

  return (
    <>
      <div
        className={`pointer-events-none fixed inset-0 ${backdropMotionClass} ${
          uiOpen ? 'z-[66]' : 'z-[61]'
        } ${uiOpen ? 'opacity-100' : 'opacity-0'}`}
        aria-hidden={!uiOpen}
      >
        <div className="absolute inset-0 bg-slate-900/25 dark:bg-black/40" />
      </div>

      <div
        ref={shellRef}
        className={`pointer-events-none fixed bottom-0 top-16 flex w-max max-w-[100vw] items-stretch p-2 pb-3 pt-2 ${shellMotionClass} ${
          isRtl ? 'right-0 ps-2.5 pe-2' : 'left-0 ps-2.5 pe-2'
        } ${uiOpen ? 'z-[67]' : 'z-[62]'} ${
          swapHidden || directionJustChanged ? 'invisible opacity-0' : ''
        } ${
          uiOpen ? 'translate-x-0' : isRtl ? 'translate-x-full' : '-translate-x-full'
        }`}
      >
        <nav
          id={panelId}
          data-tour-id="side-nav-panel"
          role="dialog"
          aria-modal="false"
          aria-label={t('mainNav')}
          dir={direction}
          className={`learner-side-nav-panel learner-side-nav-glass flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border border-slate-200/70 shadow-2xl dark:border-slate-600/60 ${
            uiOpen ? 'pointer-events-auto' : 'pointer-events-none'
          }`}
        >
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="shrink-0 border-b border-slate-200/50 px-4 py-3 dark:border-slate-700/50">
            <div className="flex items-center gap-3" dir={direction}>
              <button
                type="button"
                onClick={() => runAndClose(onHomeClick)}
                className="isolate flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-white/30 bg-gradient-to-b from-white/20 via-white/15 to-white/10 shadow-lg ring-2 ring-amber-400/25 backdrop-blur-md"
                aria-label={tCommon('goToHome')}
                title={tCommon('goToHome')}
              >
                <AppBrandLogo onCircle className="h-[2.35rem] w-[2.35rem]" alt="" />
              </button>
              <div className="min-w-0 flex-1 text-start">
                <p className="learner-chrome-readable truncate text-sm font-semibold dark:text-slate-100">
                  {displayName}
                </p>
                <p className="learner-chrome-readable truncate text-[11px] dark:text-slate-400">{t('learnerTagline')}</p>
              </div>
              <LanguageSwitcher variant="compact" className="shrink-0" persistUser />
            </div>
          </div>

          <div className="learner-side-nav-scroll flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-y-contain px-2 py-2.5">
            <SideNavItem icon={Home} label={t('home')} onClick={() => runAndClose(onHomeClick)} />
            {onDashboardClick && (
              <SideNavItem
                icon={LayoutDashboard}
                label={t('learningDashboard')}
                onClick={() => runAndClose(onDashboardClick)}
              />
            )}
            {onOpenCourses && (
              <SideNavItem icon={BookOpen} label={t('courses')} onClick={() => runAndClose(onOpenCourses)} />
            )}

            <NavSeparator />

            <SideNavItem
              icon={User}
              label={t('myProfile')}
              onClick={() => {
                onNavigateProfile?.(simpleHome ? 'menus' : 'profile');
                close();
              }}
            />
            <SideNavItem
              icon={Mail}
              label={t('messages')}
              badge={unreadMessageCount}
              onClick={() => {
                onNavigateProfile?.('messages');
                close();
              }}
            />
            <SideNavItem
              icon={Heart}
              label={t('favorites')}
              onClick={() => {
                onNavigateProfile?.('favorites');
                close();
              }}
            />
            <SideNavItem icon={Library} label={t('library')} onClick={() => runAndClose(onOpenLibrary)} />

            <NavSeparator />

            {showEdgeTools ? (
              <>
                <SideNavItem
                  icon={SquareStack}
                  label={tLeitner('title')}
                  badge={leitnerDue}
                  tourId="side-nav-leitner"
                  onClick={() => {
                    window.dispatchEvent(new CustomEvent(LEITNER_OPEN_EVENT));
                    close();
                  }}
                />
                <SideNavItem
                  icon={Send}
                  tourId="side-nav-telegram"
                  label={(() => {
                    const value = tCommon('edgeActions.telegram', { keySeparator: false, defaultValue: '' });
                    if (value && value !== 'edgeActions' && !value.startsWith('edgeActions.')) return value;
                    return isRtl ? 'تلگرام' : 'Telegram';
                  })()}
                  onClick={() => {
                    window.dispatchEvent(new CustomEvent(LEARNER_OPEN_TELEGRAM_EVENT));
                    close();
                  }}
                />
                <SideNavItem
                  icon={MessageCircle}
                  tourId="side-nav-chat"
                  label={tCommon('aiChat')}
                  onClick={() => {
                    window.dispatchEvent(new CustomEvent(LEARNER_OPEN_CHAT_EVENT));
                    close();
                  }}
                />
              </>
            ) : (
            <>
            <button
              type="button"
              data-tour-id="side-nav-public-pages"
              onClick={() => setPublicExpanded((e) => !e)}
              className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-start text-sm font-medium transition-colors hover:bg-white/40 dark:text-slate-100 dark:hover:bg-slate-800/40"
              aria-expanded={publicExpanded}
              aria-controls={publicPagesPanelId}
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-500/15 text-amber-800 dark:bg-amber-500/20 dark:text-amber-200">
                <FileText className="h-[18px] w-[18px]" aria-hidden />
              </span>
              <span className="learner-chrome-readable flex-1 dark:text-slate-100">{t('publicPages')}</span>
              <ChevronDown
                className={`learner-chrome-icon h-4 w-4 shrink-0 transition-transform duration-200 ease-out dark:text-slate-400 ${
                  publicExpanded ? 'rotate-180' : ''
                }`}
                aria-hidden
              />
            </button>

            <CollapsibleFadePanel expanded={publicExpanded} panelId={publicPagesPanelId}>
                <ul className="mb-1 ms-3 space-y-0.5 border-s-2 border-amber-400/35 ps-3 dark:border-amber-500/30">
                  {publicPageLinks.map(({ key, label }) => (
                    <li key={key}>
                      <button
                        type="button"
                        onClick={() => runAndClose(() => onOpenStaticPage?.(key))}
                        className="learner-chrome-readable w-full rounded-lg px-2 py-1.5 text-start text-sm transition-colors hover:bg-white/35 hover:text-amber-900 dark:text-slate-300 dark:hover:bg-slate-800/35 dark:hover:text-amber-200"
                      >
                        {label}
                      </button>
                    </li>
                  ))}
                </ul>
            </CollapsibleFadePanel>
            </>
            )}
          </div>

          <div className="shrink-0 border-t border-slate-200/50 px-3 py-2.5 text-center dark:border-slate-700/50">
            <p className="learner-chrome-readable text-[11px] text-slate-500 dark:text-slate-400">
              {versionLabel ? t('version', { label: versionLabel }) : t('versionLoading')}
            </p>
          </div>
        </div>
        </nav>
      </div>
    </>
  );
};

function NavSeparator() {
  return <div className="my-1.5 h-px shrink-0 bg-slate-300/50 dark:bg-slate-600/50" role="separator" />;
}

function SideNavItem({
  icon: Icon,
  label,
  onClick,
  badge = 0,
  tourId,
}: {
  icon: LucideIcon;
  label: string;
  onClick: () => void;
  badge?: number;
  tourId?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      {...(tourId ? { 'data-tour-id': tourId } : {})}
      className="flex w-full shrink-0 items-center gap-3 rounded-xl px-3 py-2.5 text-start text-sm font-medium transition-colors hover:bg-white/40 dark:text-slate-100 dark:hover:bg-slate-800/40"
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-500/10 dark:bg-slate-400/10 dark:text-slate-200">
        <Icon className="learner-chrome-icon h-[18px] w-[18px]" aria-hidden />
      </span>
      <span className="learner-chrome-readable min-w-0 flex-1 truncate px-0.5 dark:text-slate-100">{label}</span>
      {badge > 0 && (
        <span className="flex h-5 min-w-[1.25rem] shrink-0 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </button>
  );
}

export default LearnerSideNav;
